import type { ObjectContext } from 'Shared/posters/project/objects';
import type { SelectionContext } from 'Shared/posters/canvas/selection';
import type { CanvasContext } from 'Shared/posters/canvas/canvas';
import { rgbToHex, parseFontFamily } from 'Shared/posters/canvas/utils';
import type { RuntimeObject, RuntimeImage, RuntimeText, RuntimeContainer } from 'Shared/posters/types';
import type { PosterObject } from 'Shared/posters/project/posterSchema';
import type { ImageImpl } from 'Shared/posters/canvas/images/ImageImpl';
import type { ContainerImpl } from 'Shared/posters/canvas/container/ContainerImpl';
import type { ITextImpl } from 'Shared/posters/canvas/text/AbstractTextImpl';
import { useUser } from 'Shared/SiteProvider';

export function useImportExport(objCtx: ObjectContext, selCtx: SelectionContext, canvasCtx: CanvasContext)
{
	const userCtx    = useUser();

	function exportPoster(): object {
		function serializeObjs(objs: RuntimeObject[]): PosterObject[] {
			const clones = JSON.parse(JSON.stringify(objs)) as RuntimeObject[]
			return clones.map(obj => {
				const live = selCtx.impls[obj.id]
				// Properties bound to a theme variable render with the variable's value, so the
				// live DOM no longer reflects the designer's own value. Skip the DOM readback for
				// bound keys to preserve the static fallback already present in the clone.
				const bindings = obj.bindings ?? {}

				// Update position/size from the live element registry, but always through the impl's
				// stored* reporters rather than the raw bbox. A user-mode field's live box hugs its
				// resolved content — anchored one baseline below its designed top, inset per textAlign,
				// and only as wide as the ink — so the raw bbox would bake this record's live geometry
				// into the design and re-apply it on every reload (see ObjectImpl.storedTop).
				if (live?.getBBox && obj.position) {
					const b = live.getBBox(!!userCtx)
					obj.position = { x: Math.round(live.storedLeft(b.pos.x)), y: Math.round(live.storedTop(b.pos.y)) }
					if (obj.type === 'text' && (obj as RuntimeText).style?.isField) {
						// A field stores only its width — its box height is never baked in as size.height.
						// On reload the reserved height is re-derived by wrapping the content at this width
						// and font (see reservedHeight), so a multi-line field reproduces its box without a
						// stored line count. An undefined storedWidth means the design never carried one, so
						// leave the clone's size alone rather than inventing one from the hugged box.
						const w = live.storedWidth(b.dims.width)
						if (w != null) (obj as RuntimeText).size = { width: Math.round(w) }
					} else if (obj.type !== 'text') {
						;(obj as RuntimeImage | RuntimeContainer).size = { width: Math.round(b.dims.width), height: Math.round(b.dims.height) }
					} else {
						// Plain text stores no width — wrapping is carried by softWrapPositions, and the
						// box is re-derived from font metrics on load.
						delete (obj as RuntimeText).size
					}
				}

				// Update live image src (may differ from JSON if toolbar-uploaded).
				// Uploaded images keep their data in obj.image and leave src null, so drop
				// the field rather than persisting a useless src:null to the DB.
				if (obj.type === 'image' && live && !bindings.src && !bindings.field) {
					const liveSrc = (live as unknown as ImageImpl).getImageSrc()
					if (liveSrc) (obj as RuntimeImage).src = liveSrc
					else delete (obj as RuntimeImage).src
				}

				// Persist the live crop view (zoom/stretch/pan). Omit it when at defaults so a plain
				// image stays clean in the DB rather than carrying a redundant cover-fit crop.
				if (obj.type === 'image' && live) {
					const crop = (live as unknown as ImageImpl).getCrop()
					if (crop.zoom !== 1 || crop.stretch !== 1 || crop.pan.x !== 0 || crop.pan.y !== 0)
						(obj as RuntimeImage).crop = crop
					else delete (obj as RuntimeImage).crop
				}

				// Containers: read live bg and border from DOM
				if (obj.type === 'container' && live) {
					const con = obj as RuntimeContainer
					const conImpl = live as unknown as ContainerImpl
					con.style ??= {}
					const bgEl = live.getEl?.()
					if (bgEl) {
						if (!bindings.fill) {
							if (conImpl.getHasFill()) {
								const fill = rgbToHex(getComputedStyle(bgEl).fill)
								if (fill) con.style.background = fill
								else delete con.style.background
							} else {
								delete con.style.background
							}
						}
						if (!bindings.stroke && !bindings.strokeWidth && !bindings.rx) {
							if (conImpl.getHasBorder()) {
								const stroke = rgbToHex(getComputedStyle(bgEl).stroke)
								if (stroke) {
									const sw = parseFloat(getComputedStyle(bgEl).strokeWidth) || 1
									con.style.border = `${sw}px solid ${stroke}`
									// Only a uniform radius is drawn as a <rect rx>. A per-corner shape is a <path>
									// whose radii already live in the object's style keys, so leave them be.
									if (bgEl.tagName === 'rect') {
										const rx = parseFloat(bgEl.getAttribute('rx') ?? '0') || 0
										if (rx > 0) con.style.borderRadius = rx
										else delete con.style.borderRadius
									}
								} else {
									delete con.style.border
									delete con.style.borderRadius
								}
							} else {
								delete con.style.border
								delete con.style.borderRadius
							}
						}
					}
				}

				// Text elements: read live text content and styles from DOM + signals
				if (obj.type === 'text' && live) {
					const txt = obj as RuntimeText
					const textImpl = live as unknown as ITextImpl
					const textEl = live.getTextEl?.()
					{
						// content is deliberately NOT read back from the impl here. The popup editor is the
						// sole writer of a text object's content (onTextEdit → setTextContentCmd /
						// setTemplateAndResizeCmd, both of which commit to the store), so the clone already
						// holds the authoritative template string. Reading textImpl.getText() would instead
						// return a user-mode field's *resolved* text (PlainWrap.getText → displayText), baking
						// the resolved value into content and destroying the {field} template on save/reload.
						if (textEl) {
							const cs = getComputedStyle(textEl)
							txt.style ??= {}
							if (!bindings.color) {
								const textColor = rgbToHex(cs.fill)
								if (textColor) txt.style.color = textColor
							}
							if (!bindings.fontSize) txt.style.fontSize = parseFloat(cs.fontSize)
							if (!bindings.fontFamily) {
								const ff = parseFontFamily(cs.fontFamily)
								if (ff) txt.style.fontFamily = ff
							}
							if (!bindings.fontWeight) txt.style.fontWeight = parseInt(cs.fontWeight)
							if (!bindings.letterSpacing) {
								const ls = cs.letterSpacing
								txt.style.letterSpacing = ls === 'normal' ? 0 : parseFloat(ls) || 0
							}
						}
						txt.style ??= {}
						if (!bindings.lineHeight) txt.style.lineHeight = textImpl.getLineHeight()
						txt.style.textAlign = textImpl.getTextAlign()
					}
				}

				if (obj.type === 'container') {
					const con = obj as RuntimeContainer
					if (con.objects?.length) con.objects = serializeObjs(con.objects) as unknown as RuntimeObject[]
				}

				// id is persisted (not stripped): it is the stable handle that survives save/load and
				// is carried through flattening so a flattened object can be traced to its source design
				// object. assignIds only fills gaps on load, so a saved id round-trips unchanged.
				return obj as unknown as PosterObject
			})
		}

		const c = canvasCtx.canvas()
		const canvasBindings = canvasCtx.canvasBindings()
		return {
			version: 1,
			canvas: {
				width: c.width, height: c.height, background: canvasCtx.canvasBg(),
				...(Object.keys(canvasBindings).length ? { bindings: canvasBindings } : {}),
			},
			objects: serializeObjs(objCtx.objects()),
		}
	}

	return { exportPoster }
}
